import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { FloppyDisk, Plus, Trash } from "@phosphor-icons/react";
import TagsInput from "@/components/TagsInput";

// Admin console tab — edit the advisor-plans landing (/plans) source of
// truth. Backend endpoints: GET/PUT /api/admin/advisor/plans.
export default function AdvisorPlansPanel() {
    const [settings, setSettings] = useState({
        currency: "USD",
        free_scans_default: 1,
        free_scans_label: "First client scan is on us.",
        footer_note: "",
        back_to_biz_resilience_url: "",
        back_to_license_scan_url: "",
        plans: [],
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");
    const [msg, setMsg] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/admin/advisor/plans");
                setSettings({
                    currency: data.currency || "USD",
                    free_scans_default: Number(data.free_scans_default ?? 1),
                    free_scans_label: data.free_scans_label || "First client scan is on us.",
                    footer_note: data.footer_note || "",
                    back_to_biz_resilience_url: data.back_to_biz_resilience_url || "",
                    back_to_license_scan_url: data.back_to_license_scan_url || "",
                    plans: data.plans || [],
                });
            } catch (e) {
                setErr(formatApiError(e));
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const updatePlan = (idx, patch) => {
        setSettings((s) => ({
            ...s,
            plans: s.plans.map((p, i) => (i === idx ? { ...p, ...patch } : p)),
        }));
    };

    const removePlan = (idx) => {
        if (!window.confirm("Remove this plan? This just hides it from /plans — you can re-add it later.")) return;
        setSettings((s) => ({ ...s, plans: s.plans.filter((_, i) => i !== idx) }));
    };

    const addPlan = () => {
        const nextOrder = Math.max(0, ...settings.plans.map((p) => p.sort_order || 0)) + 1;
        setSettings((s) => ({
            ...s,
            plans: [
                ...s.plans,
                {
                    slug: `plan_${Date.now()}`,
                    display_name: "New Plan",
                    description: "",
                    features: [],
                    monthly_price: 0,
                    annual_months_free: 2,
                    active: false,
                    sort_order: nextOrder,
                    highlighted: false,
                    monthly_scan_limit: 0,
                    annual_scan_limit: 0,
                    stripe_monthly_url: "",
                    stripe_annual_url: "",
                    paypal_monthly_url: "",
                    paypal_annual_url: "",
                    stripe_enabled: true,
                    paypal_enabled: true,
                },
            ],
        }));
    };

    const save = async () => {
        setSaving(true);
        setErr("");
        setMsg("");
        try {
            // Normalise: features from comma string if needed, coerce numbers
            const payload = {
                currency: settings.currency || "USD",
                free_scans_default: Number(settings.free_scans_default) || 0,
                free_scans_label: settings.free_scans_label || "",
                footer_note: settings.footer_note || "",
                back_to_biz_resilience_url: settings.back_to_biz_resilience_url || "",
                back_to_license_scan_url: settings.back_to_license_scan_url || "",
                plans: settings.plans.map((p) => ({
                    ...p,
                    monthly_price: Number(p.monthly_price) || 0,
                    annual_months_free: Number(p.annual_months_free ?? 2),
                    monthly_scan_limit: Number(p.monthly_scan_limit) || 0,
                    annual_scan_limit: Number(p.annual_scan_limit) || 0,
                    sort_order: Number(p.sort_order) || 0,
                    stripe_enabled: p.stripe_enabled !== false,
                    paypal_enabled: p.paypal_enabled !== false,
                    features: Array.isArray(p.features)
                        ? p.features
                        : String(p.features || "")
                              .split(",")
                              .map((s) => s.trim())
                              .filter(Boolean),
                })),
            };
            const { data } = await api.put("/admin/advisor/plans", payload);
            setSettings({
                currency: data.currency || "USD",
                free_scans_default: Number(data.free_scans_default ?? 1),
                free_scans_label: data.free_scans_label || "",
                footer_note: data.footer_note || "",
                back_to_biz_resilience_url: data.back_to_biz_resilience_url || "",
                back_to_license_scan_url: data.back_to_license_scan_url || "",
                plans: data.plans || [],
            });
            setMsg("Advisor plans saved. The /plans page now reflects your changes.");
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="text-[#7a7a7a]">Loading advisor plans…</div>;

    return (
        <div data-testid="advisor-plans-panel">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="text-lg font-semibold">Advisor Plans</h2>
                    <p className="text-xs text-[#7a7a7a] mt-1">
                        Public landing page: <a className="text-[#2ec5b6] hover:underline" href="/plans" target="_blank" rel="noreferrer">/plans</a>.
                        Success redirect target: <span className="font-mono">/plans/success</span>.
                    </p>
                </div>
                <div className="flex gap-3">
                    <button
                        type="button"
                        className="btn-secondary inline-flex items-center gap-2"
                        onClick={addPlan}
                        data-testid="advisor-plans-add"
                    >
                        <Plus size={16} /> Add plan
                    </button>
                    <button
                        type="button"
                        className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
                        onClick={save}
                        disabled={saving}
                        data-testid="advisor-plans-save"
                    >
                        <FloppyDisk size={16} /> {saving ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>

            {msg && (
                <div className="block-opportunity mb-4 text-sm" data-testid="advisor-plans-msg">
                    {msg}
                </div>
            )}
            {err && (
                <div className="block-risk mb-4 text-sm" data-testid="advisor-plans-error">
                    {err}
                </div>
            )}

            {settings.plans.length === 0 && (
                <div className="border border-[#2a2a2a] bg-[#0a0a0a] p-6 text-sm text-[#c2c2c2]" data-testid="advisor-plans-empty">
                    No plans configured. Click <strong>Add plan</strong> to create one.
                </div>
            )}

            {/* GLOBAL SETTINGS */}
            <div className="border border-[#2a2a2a] bg-[#141414] p-5 mb-6" data-testid="advisor-plans-global">
                <div className="text-xs uppercase tracking-widest text-[#7a7a7a] mb-4">
                    Global settings — apply to /plans page + freemium behaviour
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <label className="block">
                        <span className="block text-[11px] uppercase tracking-widest text-[#7a7a7a] mb-1.5">
                            Currency (display only)
                        </span>
                        <input
                            type="text"
                            className="input-base"
                            value={settings.currency}
                            onChange={(e) => setSettings((s) => ({ ...s, currency: e.target.value.toUpperCase() }))}
                            maxLength={6}
                            data-testid="advisor-plans-currency"
                        />
                    </label>
                    <label className="block">
                        <span className="block text-[11px] uppercase tracking-widest text-[#7a7a7a] mb-1.5">
                            Number of free client scans
                        </span>
                        <input
                            type="number"
                            className="input-base"
                            min={0}
                            max={100}
                            step={1}
                            value={settings.free_scans_default}
                            onChange={(e) =>
                                setSettings((s) => ({ ...s, free_scans_default: e.target.value }))
                            }
                            data-testid="advisor-plans-free-scans-default"
                        />
                        <span className="block text-[11px] text-[#7a7a7a] mt-1">
                            How many scans new users get before they must pick a plan. Set to 0 to force
                            immediate subscription.
                        </span>
                    </label>
                    <label className="block">
                        <span className="block text-[11px] uppercase tracking-widest text-[#7a7a7a] mb-1.5">
                            Free-scans copy on /plans
                        </span>
                        <input
                            type="text"
                            className="input-base"
                            value={settings.free_scans_label}
                            onChange={(e) => setSettings((s) => ({ ...s, free_scans_label: e.target.value }))}
                            maxLength={200}
                            data-testid="advisor-plans-free-scans-label"
                        />
                        <span className="block text-[11px] text-[#7a7a7a] mt-1">
                            Shown as the freemium tagline on the public /plans hero. Leave blank to hide.
                        </span>
                    </label>
                </div>
                <label className="block mt-4">
                    <span className="block text-[11px] uppercase tracking-widest text-[#7a7a7a] mb-1.5">
                        Footer note under plan cards
                    </span>
                    <textarea
                        rows={3}
                        className="input-base"
                        value={settings.footer_note}
                        onChange={(e) => setSettings((s) => ({ ...s, footer_note: e.target.value }))}
                        maxLength={600}
                        placeholder="e.g. The SME experience and self-serve $27 scan are unchanged. Advisor subscriptions are billed via our partner app — start a free client scan now and subscribe when you're ready to deliver."
                        data-testid="advisor-plans-footer-note"
                    />
                    <span className="block text-[11px] text-[#7a7a7a] mt-1">
                        Marketing paragraph shown directly below the plan cards on /plans. Leave blank to hide.
                    </span>
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <label className="block">
                        <span className="block text-[11px] uppercase tracking-widest text-[#7a7a7a] mb-1.5">
                            "Back to Biz Resilience" URL
                        </span>
                        <input
                            type="url"
                            className="input-base"
                            placeholder="https://bizresilience.smartnote2self.com/"
                            value={settings.back_to_biz_resilience_url}
                            onChange={(e) => setSettings((s) => ({ ...s, back_to_biz_resilience_url: e.target.value }))}
                            maxLength={1000}
                            data-testid="advisor-plans-back-biz-url"
                        />
                        <span className="block text-[11px] text-[#7a7a7a] mt-1">
                            Renders as a "&larr; Back to Biz Resilience Scan" link at the bottom of /plans. Leave blank to hide.
                        </span>
                    </label>
                    <label className="block">
                        <span className="block text-[11px] uppercase tracking-widest text-[#7a7a7a] mb-1.5">
                            "Back to License Risk Scan" URL
                        </span>
                        <input
                            type="url"
                            className="input-base"
                            placeholder="https://mlsapp.smartnote2self.com/"
                            value={settings.back_to_license_scan_url}
                            onChange={(e) => setSettings((s) => ({ ...s, back_to_license_scan_url: e.target.value }))}
                            maxLength={1000}
                            data-testid="advisor-plans-back-license-url"
                        />
                        <span className="block text-[11px] text-[#7a7a7a] mt-1">
                            Renders as a "&larr; Back to License Risk Scan overview" link at the bottom of /plans. Leave blank to hide.
                        </span>
                    </label>
                </div>
            </div>

            <div className="space-y-6">
                {settings.plans.map((p, idx) => (
                    <PlanEditor
                        key={p.slug + idx}
                        plan={p}
                        onChange={(patch) => updatePlan(idx, patch)}
                        onRemove={() => removePlan(idx)}
                    />
                ))}
            </div>
        </div>
    );
}

function PlanEditor({ plan, onChange, onRemove }) {
    const annual = Math.round(
        (Number(plan.monthly_price) || 0) * Math.max(0, 12 - (Number(plan.annual_months_free) || 0)) * 100
    ) / 100;

    return (
        <div className="border border-[#2a2a2a] bg-[#141414] p-5" data-testid={`advisor-plan-row-${plan.slug}`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Slug (URL-safe id)" testId={`advisor-plan-slug-${plan.slug}`}>
                    <input
                        type="text"
                        className="input-base"
                        value={plan.slug}
                        onChange={(e) => onChange({ slug: e.target.value })}
                    />
                </Field>
                <Field label="Display name">
                    <input
                        type="text"
                        className="input-base"
                        value={plan.display_name}
                        onChange={(e) => onChange({ display_name: e.target.value })}
                        data-testid={`advisor-plan-name-${plan.slug}`}
                    />
                </Field>
                <Field label="Description" className="md:col-span-2">
                    <textarea
                        rows={2}
                        className="input-base"
                        value={plan.description || ""}
                        onChange={(e) => onChange({ description: e.target.value })}
                        data-testid={`advisor-plan-desc-${plan.slug}`}
                    />
                </Field>
                <Field label="Features (comma-separated)" className="md:col-span-2">
                    <TagsInput
                        className="input-base"
                        value={plan.features || []}
                        onChange={(features) => onChange({ features })}
                        data-testid={`advisor-plan-features-${plan.slug}`}
                    />
                </Field>
                <Field label="1-Month pass price (USD, one-time)">
                    <input
                        type="number"
                        min={0}
                        step="0.01"
                        className="input-base"
                        value={plan.monthly_price}
                        onChange={(e) => onChange({ monthly_price: e.target.value })}
                        data-testid={`advisor-plan-monthly-${plan.slug}`}
                    />
                </Field>
                <Field label="Annual months free (2 = 2 free) — controls the 1-Year pass price">
                    <input
                        type="number"
                        min={0}
                        max={12}
                        step={1}
                        className="input-base"
                        value={plan.annual_months_free ?? 2}
                        onChange={(e) => onChange({ annual_months_free: e.target.value })}
                        data-testid={`advisor-plan-months-free-${plan.slug}`}
                    />
                </Field>
                <Field label="Client scans included with 1-Month pass">
                    <input
                        type="number"
                        min={0}
                        step={1}
                        className="input-base"
                        placeholder="e.g. 5"
                        value={plan.monthly_scan_limit ?? 0}
                        onChange={(e) => onChange({ monthly_scan_limit: e.target.value })}
                        data-testid={`advisor-plan-monthly-scans-${plan.slug}`}
                    />
                </Field>
                <Field label="Client scans included with 1-Year pass">
                    <input
                        type="number"
                        min={0}
                        step={1}
                        className="input-base"
                        placeholder="e.g. 100"
                        value={plan.annual_scan_limit ?? 0}
                        onChange={(e) => onChange({ annual_scan_limit: e.target.value })}
                        data-testid={`advisor-plan-annual-scans-${plan.slug}`}
                    />
                </Field>
                <div className="md:col-span-2 flex items-center gap-6 text-sm">
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={!!plan.active}
                            onChange={(e) => onChange({ active: e.target.checked })}
                            data-testid={`advisor-plan-active-${plan.slug}`}
                        />
                        <span>Active (shown on /plans)</span>
                    </label>
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={!!plan.highlighted}
                            onChange={(e) => onChange({ highlighted: e.target.checked })}
                            data-testid={`advisor-plan-highlighted-${plan.slug}`}
                        />
                        <span>Highlight as &quot;most popular&quot;</span>
                    </label>
                    <Field label="Sort order" className="w-24 ml-auto">
                        <input
                            type="number"
                            min={0}
                            className="input-base"
                            value={plan.sort_order || 0}
                            onChange={(e) => onChange({ sort_order: e.target.value })}
                            data-testid={`advisor-plan-order-${plan.slug}`}
                        />
                    </Field>
                </div>

                <div className="md:col-span-2 mt-4 pt-4 border-t border-[#2a2a2a]">
                    <div className="text-xs uppercase tracking-widest text-[#7a7a7a] mb-2">
                        Post-purchase claim URLs (paste into Stripe / PayPal welcome emails)
                    </div>
                    <div className="text-[11px] text-[#7a7a7a] mb-3 leading-relaxed">
                        When a buyer completes checkout, forward them the matching URL. On first
                        click (after login/register) the app credits their account with the pass&apos;s
                        scans. Each account can claim each URL once — subsequent clicks show a
                        friendly &quot;already claimed&quot; message. Review claims and revoke via
                        <strong> Admin → Pass Claims</strong>.
                    </div>
                    <ClaimUrlRow
                        slug={plan.slug}
                        cycle="monthly"
                        scans={plan.monthly_scan_limit || 0}
                    />
                    <ClaimUrlRow
                        slug={plan.slug}
                        cycle="annual"
                        scans={plan.annual_scan_limit || 0}
                    />
                </div>

                <div className="md:col-span-2 mt-2 pt-4 border-t border-[#2a2a2a]">
                    <div className="text-xs uppercase tracking-widest text-[#7a7a7a] mb-3">
                        One-time payment links (paste from Stripe / PayPal dashboards)
                    </div>
                    <div className="text-[11px] text-[#7a7a7a] mb-3">
                        These are ONE-TIME purchase links, not recurring subscriptions. 1-Year pass renders at <strong>${annual.toLocaleString("en-US")}</strong> based on {plan.annual_months_free ?? 2} months free vs. buying twelve 1-Month passes.
                        Uncheck a provider below to hide its button on /plans without deleting the URL.
                    </div>
                    <div className="flex flex-wrap items-center gap-6 text-sm mb-2">
                        <label className="inline-flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={plan.stripe_enabled !== false}
                                onChange={(e) => onChange({ stripe_enabled: e.target.checked })}
                                data-testid={`advisor-plan-stripe-enabled-${plan.slug}`}
                            />
                            <span>Stripe checkout active on /plans</span>
                        </label>
                        <label className="inline-flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={plan.paypal_enabled !== false}
                                onChange={(e) => onChange({ paypal_enabled: e.target.checked })}
                                data-testid={`advisor-plan-paypal-enabled-${plan.slug}`}
                            />
                            <span>PayPal checkout active on /plans</span>
                        </label>
                    </div>
                </div>

                <Field label="Stripe — 1-Month pass payment link">
                    <input
                        type="url"
                        className="input-base"
                        placeholder="https://buy.stripe.com/..."
                        value={plan.stripe_monthly_url || ""}
                        onChange={(e) => onChange({ stripe_monthly_url: e.target.value })}
                        data-testid={`advisor-plan-stripe-monthly-${plan.slug}`}
                    />
                </Field>
                <Field label="Stripe — 1-Year pass payment link">
                    <input
                        type="url"
                        className="input-base"
                        placeholder="https://buy.stripe.com/..."
                        value={plan.stripe_annual_url || ""}
                        onChange={(e) => onChange({ stripe_annual_url: e.target.value })}
                        data-testid={`advisor-plan-stripe-annual-${plan.slug}`}
                    />
                </Field>
                <Field label="PayPal — 1-Month pass payment link">
                    <input
                        type="url"
                        className="input-base"
                        placeholder="https://www.paypal.com/..."
                        value={plan.paypal_monthly_url || ""}
                        onChange={(e) => onChange({ paypal_monthly_url: e.target.value })}
                        data-testid={`advisor-plan-paypal-monthly-${plan.slug}`}
                    />
                </Field>
                <Field label="PayPal — 1-Year pass payment link">
                    <input
                        type="url"
                        className="input-base"
                        placeholder="https://www.paypal.com/..."
                        value={plan.paypal_annual_url || ""}
                        onChange={(e) => onChange({ paypal_annual_url: e.target.value })}
                        data-testid={`advisor-plan-paypal-annual-${plan.slug}`}
                    />
                </Field>

                <div className="md:col-span-2 flex justify-end">
                    <button
                        type="button"
                        className="text-[#c95151] hover:text-[#e26a6a] inline-flex items-center gap-2 text-sm"
                        onClick={onRemove}
                        data-testid={`advisor-plan-remove-${plan.slug}`}
                    >
                        <Trash size={14} /> Remove plan
                    </button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, className = "", testId, children }) {
    return (
        <label className={`block ${className}`} data-testid={testId}>
            <span className="block text-[11px] uppercase tracking-widest text-[#7a7a7a] mb-1.5">{label}</span>
            {children}
        </label>
    );
}

function ClaimUrlRow({ slug, cycle, scans }) {
    const [copied, setCopied] = useState(false);
    const url = `${window.location.origin}/claim/${slug}/${cycle}`;
    const label = cycle === "annual" ? "1-Year pass" : "1-Month pass";
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        } catch {
            // fallback — user can select and copy manually
        }
    };
    return (
        <div className="flex items-center gap-2 mb-2" data-testid={`claim-url-row-${slug}-${cycle}`}>
            <div className="text-[11px] font-mono text-[#c2c2c2] w-24 shrink-0 uppercase tracking-widest">
                {label} · {scans || 0} scans
            </div>
            <input
                readOnly
                className="input-base flex-1 font-mono text-xs"
                value={url}
                onFocus={(e) => e.target.select()}
                data-testid={`claim-url-input-${slug}-${cycle}`}
            />
            <button
                type="button"
                onClick={copy}
                className="btn-secondary text-xs whitespace-nowrap"
                data-testid={`claim-url-copy-${slug}-${cycle}`}
            >
                {copied ? "Copied" : "Copy"}
            </button>
        </div>
    );
}
